import { observer } from "mobx-react-lite";
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from "react-i18next";
import { Button, Form, Input, InputNumber, message, Modal, Pagination, Select } from "antd";
import { LevelSettings, QuestionForm } from "../../pages/Tests/EditOrAddTest";
import { useEffect, useMemo, useState } from "react";
import MonacoEditor from "@monaco-editor/react";

const defaultValueQuestion: QuestionForm = {
    id: 0,
    question: '',
    language: 'en',
    numberOfQuestion: 1,
    options: [],
    prompt: '',
    numberPoints: 1,
    code: '',
    correctAnswer: '',
    type: 'singleChoice',
}

export const AddTestFormQustions = observer(({ questions, setQuestions, isOneLanguageQuestions, handleFinish, currentStep, isEditProcess, setCurrentStep }:
    {
        questions: QuestionForm[],
        currentStep: number;
        isEditProcess: boolean;
        isOneLanguageQuestions: boolean;
        setQuestions: (questions: QuestionForm[]) => void;
        setCurrentStep: (step: number) => void;
        handleFinish: () => void;
    }) => {
    const [formQuestions] = Form.useForm();
    const { t } = useTranslation();
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [newQuestionType, setNewQuestionType] = useState<'singleChoice' | 'multipleChoice' | 'writeCode' | 'fixCode' | 'openAnswer'>('singleChoice');
    const [newQuestion, setNewQuestion] = useState<QuestionForm>(defaultValueQuestion);
    const [currentLanguages, setCurrentLanguages] = useState<{ [key: number]: string }>({});

    useEffect(() => {
        formQuestions.setFieldsValue({
            questions: questions.map((q) => ({
                id: q.id,
                numberOfQuestion: q.numberOfQuestion,
                language: q.language,
                question: q.question,
                options: q.options,
                prompt: q.prompt,
                code: q.code,
                correctAnswer: q.correctAnswer,
            })),
        });
    }, [questions, formQuestions]);

    const groupQuestionsByNumber = (questions: QuestionForm[], isOneLanguage: boolean) => {
        const grouped: { [key: number]: QuestionForm[] } = {};

        questions.forEach((question) => {
            if (!grouped[question.numberOfQuestion]) {
                grouped[question.numberOfQuestion] = [];
            }
            grouped[question.numberOfQuestion].push(question);
        });

        if (isOneLanguage) {
            const firstLanguage = questions[0]?.language;
            for (const key in grouped) {
                grouped[key] = grouped[key].filter(q => q.language == firstLanguage);
            }
        }
        
        return grouped;
    };

    const groupedQuestions = useMemo(() => groupQuestionsByNumber(questions, isOneLanguageQuestions), [questions, isOneLanguageQuestions]);

    const handleLanguageChange = (numberOfQuestion: number, language: string) => {
        setCurrentLanguages((prev) => ({
            ...prev,
            [numberOfQuestion]: language,
        }));
    };

    const getQuestionByLanguage = (numberOfQuestion: number, language: string) => {
        const group = groupedQuestions[numberOfQuestion];
        return group ? group.find((q) => q.language === language) : null;
    };

    const getCurrentPageQuestions = () => {
        const startIndex = (currentPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;

        return Object.keys(groupedQuestions).slice(startIndex, endIndex);
    };

    const handleAddQuestion = () => {
        setIsModalVisible(true);
    };

    const handleEditQuestion = (index: number) => {
        setEditingIndex(index);
        setNewQuestion(questions[index]);
        setNewQuestionType(questions[index].type);
        setIsModalVisible(true);
    };

    const validateQuestion = (): boolean => {

        if (newQuestion.question.trim().length === 0) {
            message.error(t("add_test.PleaseEnterQuestion"));
            return false;
        }

        if (newQuestion.type == "singleChoice" || newQuestion.type == "multipleChoice") {
            if (!newQuestion.options || newQuestion.options.length < 2) {
                message.error(t("add_test.AddAtLeastTwoOptions"));
                return false;
            }
        }

        const isDuplicateQuestion = questions.some((q, index) =>
            index != editingIndex && q.question.trim().toLowerCase() === newQuestion.question.trim().toLowerCase()
        );

        if (isDuplicateQuestion) {
            message.error(t("add_test.QuestionWithSuchNameAlreadyExists"));
            return false;
        }

        if (newQuestion.options && newQuestion.options.length > 0) {
            const uniqueOptions = new Set(newQuestion.options.map(option => option.trim().toLowerCase()));
            if (uniqueOptions.size !== newQuestion.options.length) {
                message.error(t("add_test.DuplicateOptionsNotAllowed"));
                return false;
            }
        }

        return true;
    };

    const handleOk = () => {
        if (!validateQuestion()) {
            return;
        }
    
        if (editingIndex != null) {
            const updatedQuestions = questions.map((question, index) =>
                index === editingIndex ? newQuestion : question
            );
            setQuestions(updatedQuestions);
        } else {
            const maxId = questions.length > 0 ? Math.max(...questions.map(q => q.id)) : 0;
            const maxNumberOfQuestion = questions.length > 0 ? Math.max(...questions.map(q => q.numberOfQuestion)) : 0;
    
            if (isOneLanguageQuestions) {
                const newQuestionSingleLanguage: QuestionForm = {
                    ...newQuestion,
                    id: maxId + 1,
                    language: questions[0]?.language || 'en', 
                    numberOfQuestion: maxNumberOfQuestion + 1,
                    options: newQuestion.options?.map((option) => option.trim()), 
                };
    
                const updatedQuestions = [...questions, newQuestionSingleLanguage];
                setQuestions(updatedQuestions);
            } else {
                const newQuestions: QuestionForm[] = [
                    { ...newQuestion, id: maxId + 1, language: 'en', numberOfQuestion: maxNumberOfQuestion + 1, options: newQuestion.options?.map((option) => option.trim()) },
                    { ...newQuestion, id: maxId + 2, language: 'ru', numberOfQuestion: maxNumberOfQuestion + 1, options: newQuestion.options?.map((option) => option.trim()) },
                    { ...newQuestion, id: maxId + 3, language: 'ua', numberOfQuestion: maxNumberOfQuestion + 1, options: newQuestion.options?.map((option) => option.trim()) },
                ];
    
                const updatedQuestions = [...questions, ...newQuestions];
                setQuestions(updatedQuestions);
            }
        }

        handleCancel();
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        setNewQuestion(defaultValueQuestion);
        setEditingIndex(null);
    };

    const handleQuestionChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        field: string
    ) => {
        setNewQuestion(prevState => ({
            ...prevState,
            [field]: e.target.value
        }));
    };

    const handleRemoveOption = (index: number) => {
        setNewQuestion(prevState => ({
            ...prevState,
            options: prevState.options!.filter((_, i) => i !== index),
        }));
    };

    const handleOptionChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
        const updatedOptions = [...newQuestion.options!];
        updatedOptions[index] = e.target.value;
        setNewQuestion(prevState => ({
            ...prevState,
            options: updatedOptions
        }));
    };

    const handleTypeChange = (value: 'singleChoice' | 'multipleChoice' | 'writeCode' | 'fixCode' | 'openAnswer') => {
        setNewQuestionType(value);
        setNewQuestion(prevState => ({
            ...prevState,
            type: value,
            options: value === "singleChoice" || value === "multipleChoice" ? prevState.options || [] : undefined,
            code: value === "fixCode" ? prevState.code : '',
        }));
    };


    const handleDelete = (index: number) => {
        const numberOfQuestionToDelete = questions[index].numberOfQuestion;
        const updatedQuestions = questions.filter(
            (q) => q.numberOfQuestion != numberOfQuestionToDelete
        );

        setQuestions(updatedQuestions);
    };

    return (
        <div style={{ maxWidth: "800px", margin: "0 auto", padding: "20px" }}>
            <div style={{ marginBottom: '20px', textAlign: "left" }}>
                <Button
                    type="primary"
                    onClick={handleAddQuestion}
                >
                    {t("add_test.createQuestion")}
                </Button>
            </div>

            <Form layout="vertical" form={formQuestions}>
                {getCurrentPageQuestions().map((key, index) => {
                    const numberOfQuestion = Number(key);
                    const group = groupedQuestions[numberOfQuestion];

                    if (!group || group.length === 0) return null;

                    const question = isOneLanguageQuestions ? group[0] : getQuestionByLanguage(numberOfQuestion, currentLanguages[numberOfQuestion] || group[0].language);

                    if (!question) return null;

                    const globalIndex = questions.findIndex((q) => q.id == question.id);
                    const sequentialNumber = (currentPage - 1) * pageSize + index + 1;
                    const currentLanguage = currentLanguages[numberOfQuestion] || question.language;

                    return (
                        <div
                            key={numberOfQuestion}
                            style={{
                                marginBottom: "30px",
                                padding: "20px",
                                border: "1px solid #e0e0e0",
                                borderRadius: "8px",
                                backgroundColor: "#fafafa",
                                position: "relative",
                            }}
                        >
                            {group.length > 1 && (
                                <Select
                                    value={currentLanguage}
                                    onChange={(value) => handleLanguageChange(numberOfQuestion, value)}
                                    style={{
                                        position: 'absolute',
                                        top: '6px',
                                        right: '120px',
                                        display: 'flex',
                                        gap: '10px'
                                    }}
                                >
                                    {group.map((q) => (
                                        <Select.Option key={q.language} value={q.language}>
                                            {q.language.toUpperCase()}
                                        </Select.Option>
                                    ))}
                                </Select>
                            )}

                            <div style={{
                                position: 'absolute',
                                top: '6px',
                                right: '55px',
                                display: 'flex',
                                gap: '10px'
                            }}>
                                <InputNumber
                                    min={1}
                                    max={5}
                                    value={question.numberPoints ?? 1}
                                    onChange={(value) => {
                                        const updatedQuestions = [...questions];
                                        updatedQuestions[globalIndex].numberPoints = value || 1;
                                        setQuestions(updatedQuestions);
                                    }}
                                    style={{
                                        width: "50px",
                                        borderRadius: "4px"
                                    }}
                                />
                            </div>
                            <div style={{
                                position: 'absolute',
                                top: '10px',
                                right: '10px',
                                display: 'flex',
                                gap: '10px'
                            }}>
                                <EditOutlined
                                    style={{ cursor: 'pointer', fontSize: '16px' }}
                                    onClick={() => handleEditQuestion(globalIndex)}
                                />
                                <DeleteOutlined
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => handleDelete(globalIndex)}
                                />
                            </div>

                            <Form.Item
                                label={`${t("add_test.Question")} ${sequentialNumber}`}
                                name={["questions", globalIndex, "question"]}
                                rules={[
                                    {
                                        required: true,
                                        message: t("add_test.Please input the question!"),
                                    },
                                ]}
                            >
                                <Input
                                    placeholder={t("add_test.Please input the question!")}
                                    value={question.question}
                                    onChange={(e) => {
                                        const updatedQuestions = [...questions];
                                        updatedQuestions[globalIndex].question = e.target.value;
                                        setQuestions(updatedQuestions);
                                    }}
                                />
                            </Form.Item>

                            {question.type === "singleChoice" || question.type === "multipleChoice" ? (
                                <>
                                    <div style={{ marginTop: "15px" }}>
                                        {question.options?.map((option, optionIndex) => (
                                            <Form.Item
                                                key={optionIndex}
                                                label={`${t("add_test.Option")} ${optionIndex + 1}`}
                                                name={["questions", globalIndex, "options", optionIndex]}
                                                rules={[
                                                    {
                                                        required: true,
                                                        message: t("add_test.Please input the option!"),
                                                    },
                                                ]}
                                                style={{ marginBottom: "10px" }}
                                            >
                                                <Input
                                                    placeholder={t("add_test.Please input the option!", { index: optionIndex + 1 })}
                                                    value={option}
                                                    onChange={(e) => {
                                                        const updatedQuestions = [...questions];
                                                        updatedQuestions[globalIndex].options![optionIndex] = e.target.value;
                                                        setQuestions(updatedQuestions);
                                                    }}
                                                />
                                            </Form.Item>
                                        ))}
                                    </div>

                                    <Form.Item
                                        label={t("add_test.CorrectAnswer")}
                                        name={["questions", globalIndex, "correctAnswer"]}
                                        rules={[
                                            {
                                                required: true,
                                                message: t("add_test.Please select the correct answer(s)!"),
                                            },
                                        ]}
                                    >
                                        <Select
                                            mode={question.type === "multipleChoice" ? "multiple" : undefined}
                                            placeholder={t("add_test.SelectCorrectAnswer")}
                                            onChange={(value) => {
                                                const updatedQuestions = [...questions];
                                                updatedQuestions[globalIndex].correctAnswer = value;
                                                setQuestions(updatedQuestions);
                                            }}
                                        >
                                            {question.options?.map((option, optionIndex) => (
                                                <Select.Option key={optionIndex} value={option}>
                                                    {option}
                                                </Select.Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </>
                            ) : null}

                            {question.type === "writeCode" && (
                                <Form.Item
                                    label={t("add_test.WriteCodePrompt")}
                                    name={["questions", globalIndex, "correctAnswer"]}
                                    rules={[
                                        {
                                            required: true,
                                            message: t("add_test.Please input the correct answer"),
                                        },
                                    ]}
                                >
                                    <MonacoEditor
                                        height={"300px"}
                                        theme="vs-light"
                                        value={questions[globalIndex].correctAnswer ?? ""}
                                        language="plaintext"
                                        onChange={(value) => {
                                            const updatedQuestions = [...questions];
                                            updatedQuestions[globalIndex].correctAnswer = value ?? "";
                                            setQuestions(updatedQuestions);
                                        }}
                                    />
                                </Form.Item>
                            )}

                            {question.type === "openAnswer" && (
                                <Form.Item
                                    label={t("add_test.WriteOpenAnswer")}
                                    name={["questions", globalIndex, "correctAnswer"]}
                                    rules={[
                                        {
                                            required: true,
                                            message: t("add_test.Please input the correct answer"),
                                        },
                                    ]}
                                >
                                    <Input.TextArea
                                        placeholder={t("add_test.Please input the correct answer")}
                                        rows={5}
                                        onChange={(e) => {
                                            const updatedQuestions = [...questions];
                                            updatedQuestions[globalIndex].correctAnswer = e.target.value;
                                            setQuestions(updatedQuestions);
                                        }}
                                    />
                                </Form.Item>
                            )}

                            {question.type === "fixCode" && (
                                <>
                                    <Form.Item
                                        label={t("add_test.FillCodePrompt")}
                                        name={["questions", globalIndex, "code"]}
                                        rules={[
                                            {
                                                required: true,
                                                message: t("add_test.Please input the correct answer"),
                                            },
                                        ]}
                                    >
                                        <MonacoEditor
                                            height={"300px"}
                                            theme="vs-light"
                                            value={questions[globalIndex].code ?? ""}
                                            language="plaintext"
                                            onChange={(value) => {
                                                const updatedQuestions = [...questions];
                                                updatedQuestions[globalIndex].code = value ?? "";
                                                setQuestions(updatedQuestions);
                                            }}
                                        />
                                    </Form.Item>
                                    <Form.Item
                                        label={t("add_test.Code")}
                                        name={["questions", globalIndex, "correctAnswer"]}
                                        rules={[
                                            {
                                                required: true,
                                                message: t("add_test.Please input the correct answer"),
                                            },
                                        ]}
                                    >
                                        <MonacoEditor
                                            height={"300px"}
                                            theme="vs-light"
                                            value={questions[globalIndex].correctAnswer ?? ""}
                                            language="plaintext"
                                            onChange={(value) => {
                                                const updatedQuestions = [...questions];
                                                updatedQuestions[globalIndex].correctAnswer = value ?? "";
                                                setQuestions(updatedQuestions);
                                            }}
                                        />
                                    </Form.Item>
                                </>
                            )}
                        </div>
                    )
                })}

                <div style={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
                    <Pagination
                        current={currentPage}
                        pageSize={pageSize}
                        total={Object.keys(groupedQuestions).length}
                        onChange={(page, size) => {
                            setCurrentPage(page);
                            setPageSize(size);
                            window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                        showSizeChanger
                        pageSizeOptions={['5', '10', '20', '50']}
                    />
                </div>
                <div style={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
                    {isEditProcess && (
                        <Button
                            type="primary"
                            onClick={() => setCurrentStep(currentStep - 1)}
                            style={{ backgroundColor: "red", borderColor: "red", color: "#fff", marginRight: "150px" }}
                        >
                            {t("Back")}
                        </Button>
                    )}

                    <Button type="primary" onClick={handleFinish}>
                        {t("add_test.Submit")}
                    </Button>
                </div>
            </Form>

            <Modal
                title={t("add_test.CreateNewQuestion")}
                open={isModalVisible}
                onOk={handleOk}
                onCancel={handleCancel}
            >
                <Form>
                    <Form.Item label={t("add_test.SelectQuestionType")}>
                        <Select
                            value={newQuestionType}
                            onChange={handleTypeChange}
                        >
                            <Select.Option value="singleChoice">{t("add_test.SingleChoice")}</Select.Option>
                            <Select.Option value="multipleChoice">{t("add_test.MultipleChoice")}</Select.Option>
                            <Select.Option value="writeCode">{t("add_test.WriteCode")}</Select.Option>
                            <Select.Option value="fixCode">{t("add_test.fixCode")}</Select.Option>
                            <Select.Option value="openAnswer">{t("add_test.openAnswer")}</Select.Option>
                        </Select>
                    </Form.Item>

                    <Form.Item label={t("add_test.Question")}>
                        <Input
                            value={newQuestion.question}
                            onChange={(e) => handleQuestionChange(e, 'question')}
                        />
                    </Form.Item>

                    {newQuestionType == "singleChoice" || newQuestionType == "multipleChoice" ? (
                        <Form.Item label={t("add_test.Options")}>
                            {Array.isArray(newQuestion.options) && newQuestion.options.map((option, index) => (
                                <div key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                                    <Input
                                        value={option}
                                        onChange={(e) => handleOptionChange(e, index)}
                                        placeholder={t("add_test.Option")}
                                        style={{ flex: 1 }}
                                    />
                                    <DeleteOutlined
                                        onClick={() => handleRemoveOption(index)}
                                        style={{ marginLeft: '10px' }}
                                    />
                                </div>
                            ))}
                            <Button
                                type="dashed"
                                onClick={() =>
                                    setNewQuestion(prevState => ({
                                        ...prevState,
                                        options: [...(prevState.options || []), ''],
                                    }))
                                }
                            >
                                {t("add_test.AddOption")}
                            </Button>
                        </Form.Item>
                    ) : null}
                </Form>
            </Modal>
        </div>
    )
});